/**
 * PostQueuePanel
 *
 * Shows a live list of auto-generated draft social posts from the
 * `scheduledPosts` Firestore collection. Each draft was created by the
 * `generateDailyDraftPosts` Cloud Function.
 *
 * Flow:
 *  1. Draft appears here with caption + template info
 *  2. Admin clicks "Load in Editor" → parent loads the template onto the canvas
 *  3. Admin clicks "📷 Capture for Instagram" in the control panel → exports
 *     at 1080px + uploads to Storage → status updates to 'ready'
 *  4. Admin clicks "Post to Instagram" here → calls postToInstagram Cloud Function
 */

import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, Instagram, Trash2, Edit3, CheckCircle, Clock, Send, Wand2, Eye, X } from 'lucide-react';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     bg: '#374151', color: '#9ca3af' },
  ready:     { label: 'Ready',     bg: '#1e3a5f', color: '#60a5fa' },
  published: { label: 'Published', bg: '#14532d', color: '#4ade80' },
};

const formatAge = (ts) => {
  if (!ts?.toDate) return null;
  const diff = Date.now() - ts.toDate().getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'Just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  if (hrs < 48)   return 'Yesterday';
  const d = ts.toDate();
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
};

const PostQueuePanel = ({ onLoadDraft, activeDraftId }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postingId, setPostingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateCount, setGenerateCount] = useState(2);
  const [previewPost, setPreviewPost] = useState(null);

  // Live listener
  useEffect(() => {
    const db = getFirestore();
    const q = query(
      collection(db, 'scheduledPosts'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => {
      console.error('PostQueuePanel snapshot error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handlePostToInstagram = async (post) => {
    if (!post.imageUrl) {
      alert('❌ No image captured yet. Load this draft in the editor, then click "📷 Capture for Instagram" in the control panel.');
      return;
    }
    const isRepost = post.status === 'published';
    const confirmMsg = isRepost
      ? `Repost to Instagram?\n\nThis post was already published. Reposting will create a NEW post on Instagram.\n\n"${post.caption?.slice(0, 100)}..."`
      : `Post to Instagram?\n\n"${post.caption?.slice(0, 100)}..."\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setPostingId(post.id);
    try {
      const fns = getFunctions();
      const fn = httpsCallable(fns, 'postToInstagram');
      const result = await fn({
        imageUrl: post.imageUrl,
        caption: post.fullCaption || [
          post.caption,
          '',
          post.type === 'job' ? '🔗 Find more dental jobs at coooly.co.uk' : '✨ Try Coooly free for 60 days → coooly.co.uk',
          '',
          post.hashtags,
        ].filter(s => s !== undefined).join('\n'),
        scheduledPostId: post.id,
      });
      if (result.data?.success) {
        alert(`✅ Posted to Instagram! Media ID: ${result.data.igMediaId}`);
      }
    } catch (err) {
      console.error('postToInstagram error:', err);
      alert(`❌ Instagram posting failed: ${err.message}`);
    } finally {
      setPostingId(null);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    setDeletingId(post.id);
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'scheduledPosts', post.id));
    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const fns = getFunctions();
      const fn = httpsCallable(fns, 'triggerDraftGeneration');
      const result = await fn({ count: generateCount });
      alert(`✅ ${result.data?.generated || generateCount} draft(s) generated. Refresh to see them.`);
    } catch (err) {
      console.error('Generate drafts error:', err);
      alert(`Generation failed: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#9ca3af' }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ marginRight: 12 }} />
        Loading queue...
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        <Instagram className="h-12 w-12" style={{ margin: '0 auto 16px', opacity: 0.3 }} />
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#9ca3af' }}>No drafts yet</div>
        <div style={{ fontSize: 13, marginBottom: 24 }}>
          Generate your first batch of draft posts below, or wait for the auto-generation at 9am.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <div style={{ display: 'flex', background: '#1e2130', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
            {[2, 3].map(n => (
              <button key={n} onClick={() => setGenerateCount(n)} style={{
                padding: '7px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: generateCount === n ? '#7c3aed' : 'transparent',
                color: generateCount === n ? '#fff' : '#6b7280',
              }}>{n} posts</button>
            ))}
          </div>
          <button onClick={handleGenerate} disabled={generating} style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '8px 20px',
            background: generating ? '#374151' : 'linear-gradient(135deg,#7c3aed,#6366f1)',
            border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.7 : 1,
          }}>
            {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Wand2 className="h-4 w-4" /> Generate Drafts</>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Instagram className="h-5 w-5" style={{ color: '#e1306c' }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>Instagram Queue</div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {posts.filter(p => p.status === 'draft').length} drafts ·{' '}
            {posts.filter(p => p.status === 'ready').length} ready ·{' '}
            {posts.filter(p => p.status === 'published').length} published
          </div>
        </div>

        {/* Generate drafts control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{ display: 'flex', background: '#1e2130', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
            {[2, 3].map(n => (
              <button
                key={n}
                onClick={() => setGenerateCount(n)}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: generateCount === n ? '#7c3aed' : 'transparent',
                  color: generateCount === n ? '#fff' : '#6b7280',
                  transition: 'background 0.15s',
                }}
              >
                {n} posts
              </button>
            ))}
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 18px',
              background: generating ? '#374151' : 'linear-gradient(135deg,#7c3aed,#6366f1)',
              border: 'none', borderRadius: 8, color: '#fff',
              fontWeight: 700, fontSize: 13, cursor: generating ? 'default' : 'pointer',
              opacity: generating ? 0.7 : 1, whiteSpace: 'nowrap',
            }}
          >
            {generating
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
              : <><Wand2 className="h-4 w-4" /> Generate Drafts</>
            }
          </button>
        </div>
      </div>

      {/* How-to banner */}
      <div style={{
        background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 12, color: '#a5b4fc', lineHeight: 1.6,
      }}>
        <strong>How to publish:</strong> Click <em>Load in Editor</em> → adjust canvas if needed →
        click <strong>📷 Capture for Instagram</strong> in the control panel → return here and click <strong>Post to Instagram</strong>.
      </div>

      {/* Post cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {posts.map(post => {
          const status = STATUS_CONFIG[post.status] || STATUS_CONFIG.draft;
          const isActive = post.id === activeDraftId;
          const isPosting = postingId === post.id;
          const isDeleting = deletingId === post.id;

          return (
            <div
              key={post.id}
              style={{
                background: isActive ? 'rgba(99,102,241,0.12)' : '#12151d',
                border: isActive ? '1.5px solid #6366f1' : '1px solid rgba(255,255,255,0.07)',
                borderRadius: 12,
                padding: 16,
                transition: 'border-color 0.2s',
              }}
            >
              {/* Top row: status + template ID + delete */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{
                  background: status.bg, color: status.color,
                  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>
                  {status.label}
                </span>
                {/* Post type badge */}
                <span style={{
                  background: post.type === 'job' ? '#1e3a2f' : '#1e1b3a',
                  color: post.type === 'job' ? '#4ade80' : '#a78bfa',
                  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>
                  {post.type === 'job' ? '💼 Job' : '✨ Brand'}
                </span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  {post.type === 'job'
                    ? `${post.templateData?.location || ''}`
                    : post.templateId || 'Custom'}
                </span>
                {formatAge(post.createdAt) && (
                  <span style={{ color: '#4b5563', fontSize: 11, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    {formatAge(post.createdAt)}
                  </span>
                )}
                {isActive && (
                  <span style={{ background: '#312e81', color: '#a5b4fc', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, marginLeft: formatAge(post.createdAt) ? 8 : 'auto' }}>
                    ✏️ In Editor
                  </span>
                )}
                <button
                  onClick={() => handleDelete(post)}
                  disabled={isDeleting}
                  style={{
                    marginLeft: 8, background: 'transparent', border: 'none',
                    color: '#4b5563', cursor: 'pointer', padding: 4, borderRadius: 4,
                  }}
                  title="Delete draft"
                >
                  {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>

              {/* Content row: thumbnail + text */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                {/* Background thumbnail — click to preview */}
                {(post.imageUrl || post.backgroundImageUrl) ? (
                  <div
                    onClick={() => setPreviewPost(post)}
                    title="Click to preview"
                    style={{ position: 'relative', width: 64, height: 64, flexShrink: 0, cursor: 'pointer', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <img
                      src={post.imageUrl || post.backgroundImageUrl}
                      crossOrigin="anonymous"
                      alt="Post visual"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: 0, transition: 'opacity 0.15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0}
                    >
                      <Eye className="h-5 w-5" style={{ color: '#fff' }} />
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: 64, height: 64, flexShrink: 0, borderRadius: 8,
                    background: '#1e2130', border: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Instagram className="h-6 w-6" style={{ color: '#374151' }} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {post.templateData?.mainHeading && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
                      {post.templateData.mainHeading}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5, marginBottom: 4 }}>
                    {post.caption?.slice(0, 140)}{post.caption?.length > 140 ? '…' : ''}
                  </div>
                  {post.hashtags && (
                    <div style={{ fontSize: 11, color: '#4f46e5', lineHeight: 1.4 }}>
                      {post.hashtags.slice(0, 80)}{post.hashtags?.length > 80 ? '…' : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Published badge with IG link */}
              {post.status === 'published' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, padding: '4px 10px', background: '#1a2e1a', borderRadius: 6, border: '1px solid #166534' }}>
                  <CheckCircle className="h-3.5 w-3.5" style={{ color: '#4ade80', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 600 }}>Published to Instagram</span>
                  {post.igMediaId && (
                    <a
                      href={`https://www.instagram.com/p/${post.igMediaId}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 11, color: '#818cf8', marginLeft: 'auto', textDecoration: 'underline' }}
                    >View post ↗</a>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => onLoadDraft && onLoadDraft(post)}
                  style={{
                    flex: 1, padding: '8px 0', background: '#1e293b',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                    color: '#94a3b8', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <Edit3 className="h-3.5 w-3.5" /> Load in Editor
                </button>
                <button
                  onClick={() => handlePostToInstagram(post)}
                  disabled={isPosting}
                  style={{
                    flex: 1, padding: '8px 0',
                    background: post.status === 'published'
                      ? 'rgba(99,102,241,0.15)'
                      : post.status === 'ready'
                        ? 'linear-gradient(135deg,#e1306c,#833ab4)'
                        : 'rgba(255,255,255,0.05)',
                    border: post.status === 'published'
                      ? '1px solid rgba(99,102,241,0.4)'
                      : post.status === 'ready' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: post.status === 'published' ? '#a5b4fc' : post.status === 'ready' ? '#fff' : '#4b5563',
                    fontWeight: 600, fontSize: 12, cursor: isPosting ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: isPosting ? 0.7 : 1,
                  }}
                >
                  {isPosting ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Posting…</>
                  ) : post.status === 'published' ? (
                    <><Send className="h-3.5 w-3.5" /> Repost</>
                  ) : post.status === 'ready' ? (
                    <><Send className="h-3.5 w-3.5" /> Post to Instagram</>
                  ) : (
                    <><Clock className="h-3.5 w-3.5" /> Needs Capture</>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewPost && (
        <div
          onClick={() => setPreviewPost(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#12151d',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 16,
              maxWidth: 520,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 60px rgba(0,0,0,0.7)',
            }}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  background: (STATUS_CONFIG[previewPost.status] || STATUS_CONFIG.draft).bg,
                  color: (STATUS_CONFIG[previewPost.status] || STATUS_CONFIG.draft).color,
                  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>
                  {(STATUS_CONFIG[previewPost.status] || STATUS_CONFIG.draft).label}
                </span>
                <span style={{
                  background: previewPost.type === 'job' ? '#1e3a2f' : '#1e1b3a',
                  color: previewPost.type === 'job' ? '#4ade80' : '#a78bfa',
                  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>
                  {previewPost.type === 'job' ? '💼 Job' : '✨ Brand'}
                </span>
              </div>
              <button
                onClick={() => setPreviewPost(null)}
                style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4, borderRadius: 6 }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Image */}
            {(previewPost.imageUrl || previewPost.backgroundImageUrl) && (
              <div style={{ padding: '20px 20px 0' }}>
                <img
                  src={previewPost.imageUrl || previewPost.backgroundImageUrl}
                  crossOrigin="anonymous"
                  alt="Post preview"
                  style={{ width: '100%', borderRadius: 12, display: 'block', border: '1px solid rgba(255,255,255,0.08)' }}
                />
                {!previewPost.imageUrl && previewPost.backgroundImageUrl && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, textAlign: 'center' }}>Background only — capture to generate final image</div>
                )}
              </div>
            )}

            {/* Caption + Hashtags */}
            <div style={{ padding: 20 }}>
              {previewPost.templateData?.mainHeading && (
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 10 }}>
                  {previewPost.templateData.mainHeading}
                </div>
              )}
              {previewPost.caption && (
                <div style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.6, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                  {previewPost.caption}
                </div>
              )}
              {previewPost.hashtags && (
                <div style={{ fontSize: 12, color: '#6366f1', lineHeight: 1.6, marginBottom: 16 }}>
                  {previewPost.hashtags}
                </div>
              )}

              {/* Metadata */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {previewPost.templateData?.location && (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>📍 {previewPost.templateData.location}</div>
                )}
                {previewPost.templateData?.company && (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>🏥 {previewPost.templateData.company}</div>
                )}
                {previewPost.templateData?.salary && (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>💷 {previewPost.templateData.salary}</div>
                )}
                {previewPost.createdAt?.toDate && (
                  <div style={{ fontSize: 11, color: '#4b5563' }}>Created {previewPost.createdAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  onClick={() => { onLoadDraft && onLoadDraft(previewPost); setPreviewPost(null); }}
                  style={{
                    flex: 1, padding: '9px 0', background: '#1e293b',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                    color: '#94a3b8', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <Edit3 className="h-4 w-4" /> Load in Editor
                </button>
                <button
                  onClick={() => { handlePostToInstagram(previewPost); setPreviewPost(null); }}
                  disabled={postingId === previewPost.id}
                  style={{
                    flex: 1, padding: '9px 0',
                    background: previewPost.status === 'published'
                      ? 'rgba(99,102,241,0.15)'
                      : previewPost.status === 'ready'
                        ? 'linear-gradient(135deg,#e1306c,#833ab4)'
                        : 'rgba(255,255,255,0.05)',
                    border: previewPost.status === 'published'
                      ? '1px solid rgba(99,102,241,0.4)'
                      : previewPost.status === 'ready' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: previewPost.status === 'published' ? '#a5b4fc' : previewPost.status === 'ready' ? '#fff' : '#4b5563',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <Send className="h-4 w-4" />
                  {previewPost.status === 'published' ? 'Repost' : previewPost.status === 'ready' ? 'Post to Instagram' : 'Needs Capture'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostQueuePanel;
